import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Coins, TrendingUp, Gift, Users, Copy, Check, Calendar, Trophy, ArrowRight,
  Sparkles, ChevronRight, Crown, Star, Heart, MessageCircle, Share2, ListMusic,
  UserPlus, Upload, Flame, Award, Lock, Info,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { fmtEur, fmtAriary, VOLA_PER_EUR } from "../lib/vola";

const QUEST_ICONS = {
  daily_login: Calendar,
  watch_video: Sparkles,
  like_track: Heart,
  comment: MessageCircle,
  share: Share2,
  follow_artist: UserPlus,
  add_to_playlist: ListMusic,
  create_playlist: ListMusic,
  upload_track: Upload,
  profile_complete: Crown,
  first_track: Star,
  referral_signup: Users,
  food_vote: Trophy,
  food_submit: Gift,
};

export const VolaPage = () => {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [redeeming, setRedeeming] = useState(null);
  const [copied, setCopied] = useState(false);

  const reload = async () => {
    if (!user) return;
    const [m, r, lb] = await Promise.all([
      api.get("/vola/me"),
      api.get("/vola/rewards"),
      api.get("/vola/leaderboard"),
    ]);
    setMe(m.data);
    setRewards(r.data);
    setLeaderboard(lb.data);
  };

  useEffect(() => { reload(); }, [user]);

  // Auto-claim daily login bonus on first load
  useEffect(() => {
    if (!user || !me) return;
    const today = new Date().toISOString().slice(0, 10);
    if (me.last_daily_login === today) return;
    api.post("/vola/daily-login").then(({ data }) => {
      if (data.credited > 0) {
        toast.success(`+${data.credited} Vola — Connexion jour ${data.streak} 🔥${data.bonus ? ` (bonus ${data.bonus})` : ""}`);
        reload();
      }
    }).catch(() => {});
  }, [user, me?.last_daily_login]);

  const handleRedeem = async (reward) => {
    setRedeeming(reward.reward_id);
    try {
      let contact = "";
      if (reward.kind === "cash" || reward.kind === "physical") {
        contact = window.prompt(reward.kind === "cash"
          ? "Ton numéro Mvola (ex: 034 12 345 67) ?"
          : "Ton adresse complète (Antananarivo) ?");
        if (!contact) { setRedeeming(null); return; }
      }
      const { data } = await api.post("/vola/redeem", { reward_id: reward.reward_id, contact_info: contact });
      toast.success(data.message);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Échec de l'échange");
    } finally {
      setRedeeming(null);
    }
  };

  const copyReferral = () => {
    if (!me?.referral_code) return;
    const link = `${window.location.origin}/register?r=${me.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Lien de parrainage copié !");
  };

  if (!user) {
    return (
      <div className="px-4 md:px-12 py-16 max-w-3xl mx-auto text-center" data-testid="vola-not-logged">
        <Coins size={56} className="mx-auto text-brand-yellow mb-4" />
        <h1 className="font-display text-3xl md:text-5xl font-black mb-3">Voamada Vola 💰</h1>
        <p className="text-white/70 mb-6">Gagne des points en interagissant et échange-les contre des cadeaux, du Mvola ou Voamada Premium.</p>
        <Link to="/login" className="btn-primary">Se connecter pour commencer</Link>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-12 py-8 max-w-6xl mx-auto pb-32" data-testid="vola-page">
      {/* HERO — solde + streak */}
      <div className="relative rounded-3xl bg-gradient-to-br from-brand-red via-brand-orange to-brand-yellow p-6 md:p-10 mb-8 overflow-hidden">
        <div className="absolute -top-10 -right-10 text-[200px] opacity-20 select-none">💰</div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2 text-white/90">
            <Coins size={18} />
            <span className="text-xs font-bold uppercase tracking-widest">Voamada Vola</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black text-white mb-2" data-testid="vola-balance">
            {me ? me.balance.toLocaleString("fr-FR") : "—"} <span className="text-2xl md:text-3xl">Vola</span>
          </h1>
          {/* Cagnotte en € + Ar */}
          <div className="flex items-center gap-2 mb-3" data-testid="vola-cash-equivalent">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/25 backdrop-blur text-white font-bold text-sm md:text-base">
              ≈ {fmtEur(me?.balance)}
            </span>
            <span className="text-white/75 text-xs md:text-sm">· {fmtAriary(me?.balance)}</span>
            <span className="group relative inline-flex" title={`Taux : ${VOLA_PER_EUR} Vola = 1 €`}>
              <Info size={13} className="text-white/60 hover:text-white cursor-help" />
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded-md bg-black/90 text-white text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none">
                {VOLA_PER_EUR} Vola = 1 € (≈ 5 000 Ar)
              </span>
            </span>
          </div>
          <p className="text-white/85 text-sm md:text-base mb-4">
            {me?.streak > 0 ? `🔥 Connexion jour ${me.streak} consécutif` : "Bienvenue dans le programme de récompenses Voamada"}
          </p>

          <div className="flex flex-wrap gap-3 text-sm">
            <Stat icon={<TrendingUp size={14} />} label="Total gagné" value={me?.lifetime_earned} />
            <Stat icon={<Gift size={14} />} label="Total dépensé" value={me?.lifetime_spent} />
            <Stat icon={<Flame size={14} />} label="Streak" value={me?.streak ?? 0} suffix="jours" />
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-1 p-1 bg-white/[0.03] border border-white/10 rounded-2xl mb-6 overflow-x-auto">
        {[
          { k: "dashboard", l: "🎯 Quêtes", testid: "vola-tab-dashboard" },
          { k: "rewards", l: "🎁 Récompenses", testid: "vola-tab-rewards" },
          { k: "leaderboard", l: "🏆 Classement", testid: "vola-tab-leaderboard" },
          { k: "referral", l: "👥 Parrainage", testid: "vola-tab-referral" },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`flex-1 min-w-fit px-3 md:px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition ${
              tab === t.k ? "bg-brand-red text-white" : "text-white/60 hover:text-white"
            }`}
            data-testid={t.testid}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* DASHBOARD: Quêtes */}
      {tab === "dashboard" && (
        <div className="space-y-3" data-testid="vola-quests-list">
          <h2 className="font-display text-2xl font-black mb-3">Tes quêtes du jour</h2>
          <p className="text-sm text-white/60 mb-4">Complète ces actions pour gagner des Vola. Beaucoup d'entre elles se valident automatiquement quand tu interagis sur le site.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {me?.quests?.map((q) => {
              const Icon = QUEST_ICONS[q.key] || Sparkles;
              return (
                <div key={q.key} className={`rounded-xl border p-4 transition ${
                  q.completed ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"
                }`} data-testid={`vola-quest-${q.key}`}>
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                      q.completed ? "bg-emerald-500/20 text-emerald-400" : "bg-brand-yellow/10 text-brand-yellow"
                    }`}>
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <h3 className="text-sm font-bold truncate">{q.label}</h3>
                        <span className="text-xs font-black text-brand-yellow shrink-0">+{q.amount} × {q.limit}</span>
                      </div>
                      <p className="text-[11px] text-white/50">{q.done}/{q.limit} aujourd'hui</p>
                      <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-brand-orange to-brand-yellow transition-all" style={{ width: `${q.progress}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recent transactions */}
          <h2 className="font-display text-2xl font-black mb-3 mt-10">Activité récente</h2>
          {me?.transactions?.length === 0 && <p className="text-sm text-white/40">Aucune activité pour le moment. Lance-toi !</p>}
          <ul className="space-y-1">
            {me?.transactions?.map((tx) => (
              <li key={tx.tx_id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
                <span className={`text-sm font-black ${tx.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {tx.amount >= 0 ? "+" : ""}{tx.amount}
                </span>
                <span className="text-sm text-white/80 flex-1 truncate">{tx.description || tx.kind}</span>
                <span className="text-[10px] text-white/40 shrink-0">{new Date(tx.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* REWARDS */}
      {tab === "rewards" && (
        <div className="space-y-4" data-testid="vola-rewards-list">
          <h2 className="font-display text-2xl font-black mb-3">Échange tes Vola</h2>
          <p className="text-sm text-white/60 mb-4">Choisis ce qui te tente. Les badges sont activés instantanément, le reste sous 48h.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewards.map((r) => {
              const canAfford = me && me.balance >= r.cost;
              return (
                <div key={r.reward_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex flex-col" data-testid={`vola-reward-${r.reward_id}`}>
                  <div className="text-5xl mb-3">{r.cover_url || "🎁"}</div>
                  <h3 className="font-display text-lg font-black mb-1">{r.title}</h3>
                  <p className="text-xs text-white/55 mb-4 flex-1">{r.description}</p>
                  <div className="flex items-center justify-between gap-2 mt-auto">
                    <div className="flex flex-col">
                      <span className="inline-flex items-center gap-1 text-brand-yellow font-black">
                        <Coins size={14} /> {r.cost.toLocaleString("fr-FR")}
                      </span>
                      <span className="text-[10px] text-white/45 mt-0.5">≈ {fmtEur(r.cost)}</span>
                    </div>
                    <button
                      onClick={() => canAfford && handleRedeem(r)}
                      disabled={!canAfford || redeeming === r.reward_id}
                      className={`text-xs font-bold px-3 py-2 rounded-full transition ${
                        canAfford
                          ? "bg-brand-red hover:bg-brand-orange text-white"
                          : "bg-white/5 text-white/30 cursor-not-allowed"
                      }`}
                      data-testid={`vola-redeem-${r.reward_id}`}
                    >
                      {redeeming === r.reward_id ? "..." : canAfford ? "Échanger" : <><Lock size={11} className="inline mr-1" />Manque {r.cost - (me?.balance || 0)}</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LEADERBOARD */}
      {tab === "leaderboard" && (
        <div data-testid="vola-leaderboard">
          <h2 className="font-display text-2xl font-black mb-3">Classement Voamada Vola 🏆</h2>
          <p className="text-sm text-white/60 mb-4">Top 50 utilisateurs ayant gagné le plus de Vola depuis le lancement.</p>
          <ol className="space-y-1">
            {leaderboard.map((row, i) => (
              <li key={row.user_id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition ${
                i < 3 ? "border-brand-yellow/30 bg-brand-yellow/5" : "border-white/5 bg-white/[0.02]"
              }`}>
                <span className={`shrink-0 w-8 text-center font-black ${i < 3 ? "text-brand-yellow text-lg" : "text-white/40"}`}>
                  {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                </span>
                {row.picture ? (
                  <img src={row.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center text-xs font-black">
                    {(row.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <Link to={row.is_artist ? `/artist/${row.user_id}` : "#"} className="text-sm font-bold truncate hover:text-brand-yellow">
                    {row.name}
                  </Link>
                  <p className="text-[10px] text-white/40">{row.country || "—"} · {row.streak > 0 ? `🔥 ${row.streak}j` : ""}</p>
                </div>
                <span className="font-black text-brand-yellow">{Number(row.lifetime_earned).toLocaleString("fr-FR")}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* REFERRAL */}
      {tab === "referral" && (
        <div className="space-y-4" data-testid="vola-referral">
          <h2 className="font-display text-2xl font-black mb-3">Invite tes amis</h2>
          <p className="text-sm text-white/60 mb-4">Tu gagnes <strong className="text-brand-yellow">100 Vola</strong> à chaque ami qui s'inscrit avec ton lien et complète son profil.</p>

          <div className="rounded-2xl border border-brand-yellow/30 bg-gradient-to-br from-brand-yellow/10 to-brand-orange/5 p-5">
            <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2">Ton lien de parrainage unique</p>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 px-3 py-3 rounded-xl bg-black/30 text-brand-yellow text-xs md:text-sm font-mono truncate">
                {window.location.origin}/register?r={me?.referral_code || "..."}
              </code>
              <button
                onClick={copyReferral}
                className="btn-primary inline-flex items-center gap-1.5 px-4"
                data-testid="vola-copy-referral"
              >
                {copied ? <><Check size={14} /> Copié</> : <><Copy size={14} /> Copier</>}
              </button>
            </div>
            <p className="text-[11px] text-white/50 mt-2">Code : <strong className="text-brand-yellow">{me?.referral_code}</strong> — partage-le sur WhatsApp / Facebook / TikTok</p>
          </div>

          <div className="grid md:grid-cols-3 gap-3 mt-6">
            <Step n="1" title="Partage ton lien" desc="WhatsApp, Facebook, TikTok, SMS… où tu veux." />
            <Step n="2" title="Ton ami s'inscrit" desc="Il crée son compte via ton lien — automatique." />
            <Step n="3" title="Tu gagnes 100 Vola" desc="Crédité dès qu'il complète son profil." />
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon, label, value, suffix }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-white">
    {icon}
    <span className="text-xs font-bold">{Number(value || 0).toLocaleString("fr-FR")}{suffix ? ` ${suffix}` : ""} <span className="opacity-70 font-medium">· {label}</span></span>
  </div>
);

const Step = ({ n, title, desc }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
    <div className="text-3xl font-black text-brand-orange mb-1">{n}</div>
    <p className="text-sm font-bold mb-1">{title}</p>
    <p className="text-xs text-white/50">{desc}</p>
  </div>
);

export default VolaPage;
